import { fragmentTypes, StrictTypedTypePolicies } from '@sketch/gql-types'
import { type InMemoryCacheConfig } from '@apollo/client/cache'
import { DebuggableCache } from './debug'

import { dataIdFromObject } from './dataIdFromObject'
import { ShortIdsMapper } from './ShortIdsMapper'

type RootQueryFields = NonNullable<
  NonNullable<StrictTypedTypePolicies['RootQueryType']>['fields']
>
type QueryFields = NonNullable<
  NonNullable<StrictTypedTypePolicies['Query']>['fields']
>
type SubscriptionFields = NonNullable<
  NonNullable<StrictTypedTypePolicies['RootSubscriptionType']>['fields']
>

// We have two Root Query Types - `Query` and `RootQueryType` (based on BE service)
// And Apollo sometimes doesn't know that it should search for a field within `RootQueryType`
// and searches within `Query` instead. To avoid this problem we define all fields in one place
// and use it for both `Query` and `RootQueryType` type policies
type UnifiedQueryFields = RootQueryFields & QueryFields

const unifiedQueryFields: UnifiedQueryFields = {
  comment: (existing, { args, toReference }) => {
    if (!args?.identifier) return existing
    return toReference({
      __typename: 'Comment',
      identifier: args.identifier,
    })
  },
  /**
   * Whenever a share is queried this'll let Apollo know where
   * to first check the cache if it's already present
   */
  share: (existing, { args, toReference }) => {
    if (!args?.identifier && !args?.shortId) return existing
    return toReference({
      __typename: 'Share',
      identifier: args.identifier || args.shortId,
    })
  },
  artboard: (existing, { args, toReference }) => {
    if (!args?.permanentArtboardShortId || !args?.documentVersionShortId)
      return existing
    return toReference({
      __typename: 'Artboard',
      permanentArtboardShortId: args.permanentArtboardShortId,
      documentVersionShortId: args.documentVersionShortId,
    })
  },
  workspace: (existing, { args, toReference }) => {
    if (!args?.identifier) return existing
    return toReference({
      __typename: 'Workspace',
      identifier: args.identifier,
    })
  },
  annotation: (existing, { args, toReference }) => {
    if (!args?.identifier) return existing

    return (
      existing ||
      toReference({
        __typename: 'Annotation',
        identifier: args.identifier,
      })
    )
  },
  project: (existing, { args, toReference }) => {
    if (!args?.identifier && !args?.shortId) return existing
    const identifier = args.identifier || args.shortId
    return toReference({ __typename: 'Project', identifier })
  },
  workspaceHeaviestShares: { merge: false },
}

// Remove warning by confirming default behavior of replacing field.
// For unknown reason those fields expected in the RootSubscriptionType
// can exist in other root types like Subscription but also Query (and maybe others?).
const unifiedSubscriptionFields: SubscriptionFields = {
  projectChanged: { merge: false }, // under Query type somehow
  shareChanged: { merge: false }, // under Query type somehow
}

// Subscription type does not exist in our schema but `shareCollectionChanged` field policy is
// under the Subscription type for example.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type StrictTypedTypePoliciesFixed = StrictTypedTypePolicies & {
  Subscription: StrictTypedTypePolicies['RootSubscriptionType']
}

/**
 * WARNING! BEWARE OF CUSTOM MERGE FUNCTIONS!
 *
 * It is possible to pass a custom merge function to any field policy.
 * However, if you will want to apply a custom merge function just for a certain
 * scenarios and rely on default merging for other scenarios - you might run into a problem.
 *
 * Reading docs, it seems that passing this function:
 * ```ts
 * function customMerge(existing, incoming, opts) {
 *   return opts.mergeObjects(existing, incoming)
 * }
 * ```
 * should act exactly the same as passing `merge: true`. However, it does not.
 * This function handles differently `deep merging`, `null`, `undefined` and other scenarios.
 *
 * This can lead to very subtle bugs where some fields are not merged correctly (learned the hard way).
 *
 * Suggestion: if possible, avoid custom merge functions and use `merge: true` instead. In best case scenario
 * ask `BE` to provide a field that we could use as an `id` field for the object.
 */
export const typePolicies = {
  WorkspaceFeatures: { merge: true },
  Billing: { merge: true },
  BillingCycles: { merge: true },
  BillingPrice: { merge: true },
  CloudSubscription: { merge: true },
  AppStoreSubscription: { merge: true },
  Avatar: { merge: true },
  WorkspaceMembershipsRestricted: { merge: true },
  PresentationFile: { merge: true },

  PermissionGroupRelationShares: { merge: true },
  PermissionGroupRelationProjects: { merge: true },
  PaginationMeta: { merge: true },

  Pages: { merge: true },
  Frames: { merge: true },
  Shares: { merge: true },
  Comments: { merge: true },
  Project: {
    fields: {
      previews: { merge: false },
    },
  },
  CollaborativeEditingSession: { merge: true },

  Query: {
    fields: {
      ...unifiedQueryFields,
      // Somehow some fields end up under Query instead of Subscription
      ...unifiedSubscriptionFields,
    },
  },
  RootQueryType: {
    fields: unifiedQueryFields,
  },
  Subscription: {
    fields: unifiedSubscriptionFields,
  },
  Share: {
    fields: {
      version: (existing, { args, toReference }) => {
        // BE CAREFUL. We support just a single type of cache redirect here,
        // i.e. once we ask for a specific version based on its shortId.
        // And we do not support other queries like "latest version" or preferredState: PROCESSED.
        if (!(Object.keys(args || {}).length === 1 && args?.shortId))
          return existing

        return toReference({ __typename: 'Version', shortId: args?.shortId })
      },
    },
  },
  Document: {
    fields: {
      /**
       * Whenever the artboard property of a document gets queried
       * this'll let Apollo know where to get it from the cache
       */
      artboard: (existing, { args, toReference }) => {
        if (!args?.id) return existing
        return toReference({ __typename: 'Artboard', shortId: args.id })
      },
    },
  },
} satisfies StrictTypedTypePoliciesFixed as StrictTypedTypePolicies

export const createAugmentedCache = (options: InMemoryCacheConfig = {}) => {
  const shortIdsMapper = new ShortIdsMapper()

  const merged: InMemoryCacheConfig = {
    // default values
    possibleTypes: fragmentTypes.possibleTypes,
    dataIdFromObject,
    typePolicies,
    // override values
    ...options,
  }

  return Object.assign(new DebuggableCache(merged), { shortIdsMapper })
}

export type AugmentedCache = ReturnType<typeof createAugmentedCache>

export const createCache = createAugmentedCache

export type DataIdFromObjectFn = typeof dataIdFromObject
